import { ChevronRight, MoreHoriz } from "@mui/icons-material";
import { Box, CircularProgress, Divider, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import DeleteIcon from "../../../../../../../../assets/images/icons/DeleteIcon";
import { EditIcon } from "../../../../../../../../assets/images/icons/EditIcon";
import CopyToClipboardButton from "../../../../../../../../components/common/CopyToClipboardButton/CopyToClipboardButton";
import duplicate from "../../../../../../../assets/svgs/Duplicate.svg";
import { BrandDefinition } from "../../../../../../../models/brandDefinition";
import styles from "./BrandItem.module.scss";

type BrandModalOverlayProps = {
	brand: BrandDefinition;
	isBrandTemplateEditor: boolean | undefined;
	isBrandFromPublicOrg?: boolean;
	onShowEdit: () => void;
	onDuplicate: () => void;
	onDelete: () => Promise<void> | void;
	isDeleting?: boolean;
};

const BrandModalOverlay = ({
	brand,
	isBrandTemplateEditor,
	isBrandFromPublicOrg,
	onShowEdit,
	onDuplicate,
	onDelete,
	isDeleting,
}: BrandModalOverlayProps) => {
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const { t } = useTranslation("translation", { keyPrefix: "editor.tabs.brands" });

	const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();
		setAnchorEl(event.currentTarget);
	};
	const handleMenuClose = (event?: React.MouseEvent<HTMLButtonElement>) => {
		event && event.stopPropagation();
		setAnchorEl(null);
	};

	return (
		<Box className={`${styles.actions} ${anchorEl ? styles.actionsVisible : ""}`}>
			<IconButton className={styles.kebabButton} onClick={handleMenuOpen} size="small">
				<MoreHoriz className={styles.moreIcon} />
			</IconButton>
			<Menu
				onClick={(e) => {
					e.stopPropagation();
				}}
				classes={{ paper: styles.paper }}
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={(e: any) => {
					e && e.stopPropagation();
					handleMenuClose(e);
				}}
			>
				<Box className={styles.brandNameWrapper}>
					<Typography className={styles.brandNameLabel}>{brand.name}</Typography>
				</Box>

				<Box className={styles.copyId}>
					<Typography className={styles.brandNameId}>{t("id")}</Typography>
					<Typography className={styles.brandNameId}>{brand.id}</Typography>
					<CopyToClipboardButton className={styles.copyIcon} textToCopy={brand.id?.toString()} />
				</Box>
				<Divider />
				<Box>
					<MenuItem
						onClick={(event?: any) => {
							event && event.stopPropagation();
							onShowEdit();
							handleMenuClose(event);
						}}
						classes={{ root: styles.menuItemRoot }}
					>
						<Box className={styles.menuIconWrapper}>
							{isBrandTemplateEditor && !isBrandFromPublicOrg ? (
								<EditIcon className={styles.editIcon} />
							) : (
								<ChevronRight className={styles.arrowIcon} />
							)}
							<Typography className={styles.menuLabel}>
								{isBrandTemplateEditor && !isBrandFromPublicOrg ? t("edit") : t("show")}
							</Typography>
						</Box>
					</MenuItem>

					{isBrandTemplateEditor && !isBrandFromPublicOrg && (
						<MenuItem
							onClick={(e: any) => {
								e.stopPropagation();
								onDuplicate();
								handleMenuClose(e);
							}}
							classes={{ root: styles.menuItemRoot }}
						>
							<Box className={styles.menuIconWrapper}>
								<img src={duplicate} />
								<Typography className={styles.menuLabel}>{t("duplicate")}</Typography>
							</Box>
						</MenuItem>
					)}

					{isBrandTemplateEditor && !isBrandFromPublicOrg && (
						<MenuItem
							onClick={async (e: any) => {
								e.stopPropagation();
								try {
									await onDelete();
									handleMenuClose(e);
								} catch (error) {
									console.error("Delete operation failed:", error);
								}
							}}
							classes={{ root: styles.menuItemRoot }}
						>
							<Box className={styles.menuIconWrapper}>
								{isDeleting && <CircularProgress size={16} className={styles.loader} />}
								<DeleteIcon className={styles.deleteIcon} />
								<Typography className={styles.menuLabel}>{t("delete")}</Typography>
							</Box>
						</MenuItem>
					)}
				</Box>
			</Menu>
		</Box>
	);
};

export default BrandModalOverlay;
